import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NoResultsIcon } from '@rsos/assets/icons';
import { Box, Button, Checkbox, Modal } from '@rsos/base-components';
import {
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '@rsos/base-components/Modal/ModalComponents';
import InputText from '@rsos/base-components/form/inputs/InputText';
import { useFlag } from '@rsos/flags';
import { useCallQueueFilters } from '@rsos/reduxDataStore/src/features/selectors/callQueueSelector';
import { updateUserAttributes } from '@rsos/sinatra';
import { reduceMatchingTerms } from '@rsos/utils/reduceMatchingTerms';
import { captureException } from '@rsos/utils/sentry';
import { notifyError } from '@rsos/utils/toastUtils';
import useIsIRP from '@rsos/utils/useIsIRP';
import { ctmEnableErrorMsg } from '../constants';
import { trackCTMEnabled } from '../ctmTracking';
import {
  ErrorText,
  IconWrapper,
  InputLabel,
  ModalDescription,
  ModalTitle,
  NoResultText,
  RememberPositionBox,
  Result,
  ResultsContainer,
  ResultWrapper,
} from './Modal.styles';
import ModalSwitch from './ModalSwitch';

/**
 * Call taker mode - Position ID modal.
 * @param {Boolean} isOpened - Is the modal open?
 * @param {Array.<String>} positionIDs - An Array of workstation positions.
 * @param {Function} togglePositionIDSelectionModal - Callback function to toggle the modal.
 */
const PositionIDSelectionModal = ({
  isOpened = false,
  positionIDs = [],
  togglePositionIDSelectionModal = () => {},
}) => {
  const dispatch = useDispatch();
  const callTakerModeSettings = useSelector(
    state =>
      state?.sinatra?.user?.profile?.attributes?.capstone
        ?.call_taker_mode_settings,
  );
  const [searchTerm, setSearchTerm] = useState(
    callTakerModeSettings?.remember_workstation_position &&
      callTakerModeSettings?.workstation_position
      ? callTakerModeSettings.workstation_position
      : '',
  );
  const [searchResults, setSearchResults] = useState([]);
  const [rememberPosition, setRememberPosition] = useState(
    callTakerModeSettings?.remember_workstation_position || false,
  );
  const [autoSwitchToNextCall, setAutoSwitchToNextCall] = useState(
    callTakerModeSettings?.auto_switch_to_next_call || false,
  );
  const [hasError, setHasError] = useState(false);
  const { toggleSetCallFilter } = useCallQueueFilters();
  const hasCallQueueFiltersEnabled = useFlag('call_queue_filters');
  const [filterCallToSeatPosition, setFilterCallToSeatPosition] = useState(
    hasCallQueueFiltersEnabled,
  );
  const { irpVersion } = useIsIRP();
  const { currentRole } = useSelector(state => state.sinatra.user);
  const applicationName = currentRole.application;
  const isSubmitButtonDisabled = useMemo(
    () => hasError || searchResults.length > 0 || !searchTerm,
    [hasError, searchResults.length, searchTerm],
  );

  // Check the Position ID search term against the list of available Position ID's.
  useEffect(() => {
    const foundOption = positionIDs?.find(
      option =>
        option.toLocaleLowerCase().trim() === searchTerm.toLowerCase().trim(),
    );
    if (!foundOption && searchTerm) {
      const newSearchResult = reduceMatchingTerms(positionIDs, searchTerm);
      if (newSearchResult.length) {
        setSearchResults(newSearchResult);
        setHasError(!newSearchResult.length);
      } else {
        setHasError(true);
      }
    }
    if (foundOption || !searchTerm) {
      if (hasError) setHasError(false);
      if (searchResults.length) setSearchResults([]);
    }
  }, [searchTerm, positionIDs, hasError, searchResults.length]);

  const handleInputChange = e => setSearchTerm(e.target.value);

  const handleRememberPosition = () => setRememberPosition(prev => !prev);

  const handleAutoSwitchToNextcall = () =>
    setAutoSwitchToNextCall(prev => !prev);

  const handleSubmit = async () => {
    const foundOption = positionIDs?.find(
      option =>
        option.toLocaleLowerCase().trim() === searchTerm.toLowerCase().trim(),
    );
    if (foundOption) {
      const data = {
        call_taker_mode_settings: {
          auto_switch_to_next_call: autoSwitchToNextCall,
          is_enabled: true,
          remember_workstation_position: rememberPosition,
          workstation_position: searchTerm,
        },
      };
      try {
        await dispatch(updateUserAttributes(applicationName, data));
        trackCTMEnabled(true, searchTerm, irpVersion);
      } catch (err) {
        notifyError(ctmEnableErrorMsg);
        captureException(err);
      } finally {
        togglePositionIDSelectionModal();
        toggleSetCallFilter('seatPosition', filterCallToSeatPosition);
      }
    }
  };

  return (
    <Modal
      id="call-taker-mode-position-id-modal"
      isOpened={isOpened}
      onClose={togglePositionIDSelectionModal}
      padding="45px 45px 35px"
      width={400}
    >
      <ModalHeader onClose={togglePositionIDSelectionModal}>
        <Box marginBottom="19px">
          <ModalTitle data-name="modal-title">Call-Taker Mode</ModalTitle>
          <ModalDescription data-name="modal-description">
            You are about to enter call-taker mode. In this mode, all calls that
            you answer from your seat position will automatically open the
            related event in RapidSOS portal.
          </ModalDescription>
        </Box>
      </ModalHeader>
      <ModalBody>
        <Box position="relative">
          <Box data-name="position-id">
            <InputLabel data-name="input-label">Enter Position ID</InputLabel>
            <InputText
              data-name="input-text"
              error={hasError}
              onChange={handleInputChange}
              shouldDisplayClearButton={false}
              shouldDisplayErrorIcon={false}
              type="text"
              value={searchTerm}
            />
          </Box>
          <ResultsContainer isVisible={searchResults.length || hasError}>
            {hasError ? (
              <Box data-name="no-results" padding="8px 16px 16px">
                <Box align="center" horizontal marginY="8px">
                  <IconWrapper>
                    <NoResultsIcon />
                  </IconWrapper>
                  <NoResultText>No Results</NoResultText>
                </Box>
                <ErrorText>
                  0 Results found. Please make sure that the position ID is
                  spelled correctly.
                </ErrorText>
              </Box>
            ) : (
              <>
                {searchResults.map(id => (
                  <ResultWrapper
                    data-name="search-results"
                    key={id}
                    onClick={() => setSearchTerm(id)}
                  >
                    <Result data-name={`position-id-${id}`}>{id}</Result>
                  </ResultWrapper>
                ))}
              </>
            )}
          </ResultsContainer>
          <RememberPositionBox data-name="remember-position">
            <Checkbox
              isChecked={rememberPosition}
              label="Remember position?"
              noHoverBG
              onChange={handleRememberPosition}
            />
          </RememberPositionBox>
          <ModalSwitch
            label="Auto-switch to next call"
            isOn={autoSwitchToNextCall}
            onClick={handleAutoSwitchToNextcall}
            dataName="auto-switch-to-next-call"
          />
          {hasCallQueueFiltersEnabled && (
            <ModalSwitch
              label="Filter calls to seat position"
              isOn={filterCallToSeatPosition}
              onClick={() =>
                setFilterCallToSeatPosition(!filterCallToSeatPosition)
              }
              dataName="filter-calls-to-seat-position"
            />
          )}
        </Box>
      </ModalBody>
      <ModalFooter>
        <Button
          btnType="primary"
          dataName="position-id-submit-button"
          disabled={isSubmitButtonDisabled}
          grow
          noMargin
          onClick={handleSubmit}
        >
          Submit
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default PositionIDSelectionModal;
